import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    IOnboardingCompletion,
    IOnboardingCompletionData,
    IOnboardingUserCompletion,
} from 'Source/app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { FiOnboardingCompletionStateType } from 'Source/app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-enums';
import { FiInfiniteScrollDataSource, TSortOrder } from 'Source/app/shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiItemsListType } from 'Source/app/shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from 'Source/app/shared/types';
import { FiOnboardingCompletionHelperService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-helper.service';
import { FiOnboardingCompletionSyncService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-sync.service';

@Component({
    selector: 'fi-onboarding-synchronized-assignees-list',
    templateUrl: './fi-onboarding-synchronized-assignees-list.component.html',
    styleUrl: './fi-onboarding-synchronized-assignees-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-synchronized-assignees-list' },
    standalone: false,
})
export class FiOnboardingSynchronizedAssigneesListComponent
    extends FiInfiniteScrollDataSource<IOnboardingUserCompletion>
    implements OnInit
{
    @Input({ required: true }) onboardingCompletionData: IOnboardingCompletionData;
    @Output() currentUserCompletionChange = new EventEmitter<IOnboardingUserCompletion>();

    itemsListType = FiItemsListType.OnboardingSynchronized;
    objectType = ObjectTypes.Onboarding;

    protected totalStepsCount: number;
    protected completedStepsCount: number;

    protected readonly UserColumnName = 'Name';
    protected get onboardingCompletion(): IOnboardingCompletion {
        return this.onboardingCompletionData.Onboarding;
    }

    protected get assignedUserCompletions(): IOnboardingUserCompletion[] {
        return this.onboardingCompletionData.AssignedUserCompletions;
    }

    constructor(
        injector: Injector,
        private fiOnboardingCompletionHelperService: FiOnboardingCompletionHelperService,
        private fiOnboardingCompletionSyncService: FiOnboardingCompletionSyncService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setOnboardingProgress();
        this.reloadData();
        this.subscribeOnCompletionChangedEvent();
    }

    private subscribeOnCompletionChangedEvent() {
        this.assignedUserCompletions.forEach((userCompletion) => {
            this.fiOnboardingCompletionSyncService.subscribeToOnboardingCompletionChanged({
                onboardingId: this.onboardingCompletion.Id,
                userId: userCompletion.UserId,
                userCompletionSteps: userCompletion.Steps,
                onDestroy$: this.ngOnDestroy$,
                callback: (updatedCompletions) => {
                    this.fiOnboardingCompletionSyncService.updateUserCompletions(updatedCompletions);
                    this.setOnboardingProgress();
                    this.cdr.markForCheck();
                },
            });
        });
    }

    getListData(): Promise<IOnboardingUserCompletion[]> {
        return Promise.resolve(this.onboardingCompletionData.AssignedUserCompletions);
    }

    protected getSortOrderFn(): TSortOrder<IOnboardingUserCompletion> {
        if (this.selectedSort.active === this.UserColumnName) return this.getUserNameSort.bind(this);

        return super.getSortOrderFn();
    }

    private getUserNameSort(userCompletion: IOnboardingUserCompletion): string {
        return userCompletion.UserProfile.FullName;
    }

    protected get assigneesCount(): number {
        return this.onboardingCompletionData.AssignedUserCompletions.length;
    }

    protected getTotalStepsCountForAssignee(): number {
        return this.getOnboardingStepsCount();
    }

    protected getCompletedUserStepsCount(userCompletion: IOnboardingUserCompletion): number {
        return userCompletion.Steps.filter(
            (userCompletionUserStep) =>
                userCompletionUserStep.CompletionState === FiOnboardingCompletionStateType.Completed,
        ).length;
    }

    protected onStartOnboardingClick(userCompletion: IOnboardingUserCompletion) {
        //this.currentUserCompletion = userCompletion;
        this.currentUserCompletionChange.emit(userCompletion);
    }

    private setOnboardingProgress() {
        this.totalStepsCount = this.getOnboardingStepsCount() * this.assignedUserCompletions.length;
        const userCompletedStepsCount = this.assignedUserCompletions.reduce(
            (partialSum, item) => partialSum + this.getCompletedUserStepsCount(item),
            0,
        );
        this.completedStepsCount = userCompletedStepsCount;
    }

    private getOnboardingStepsCount(): number {
        return this.fiOnboardingCompletionHelperService.getOnboardingStepsCount(this.onboardingCompletion);
    }
}
